import { useAutoAnimate } from "@formkit/auto-animate/react";
import { BellSlashIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { selectCognitoId } from "@/redux/app/appSelectors";
import { useGetUserNotificationsQuery } from "@/shared/api/notifications";
import { CircularProgress } from "@/shared/components/ui/circular-progress";
import { Pagination } from "@/shared/components/ui/pagination";
import { useReadNotifications } from "@/shared/hooks/useReadNotifications";

import Notification from "./-components/Notification";

const ITEMS_PER_PAGE = 12;

const NotificationErrorState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <BellSlashIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`There was an issue loading your notifications. Please try again later or contact your RM if the problem persists.`}</p>
    </div>
  );
};

const NotificationEmptyState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <BellSlashIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`There are no notifications available.`}</p>
    </div>
  );
};

function NotificationListView() {
  const [page, setPage] = useState(1);
  const [parentRef] = useAutoAnimate();

  const cognitoId = useSelector(selectCognitoId);

  const { data: notifications, isLoading } = useGetUserNotificationsQuery(
    { cognitoId: cognitoId! },
    { skip: !cognitoId },
  );

  const slicedNotifications = useMemo(
    () => notifications?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE) ?? [],
    [notifications, page],
  );

  useReadNotifications(slicedNotifications);

  if (isLoading) {
    return <CircularProgress className="absolute inset-0 m-auto" />;
  }

  if (!notifications) {
    return <NotificationErrorState />;
  }

  if (notifications.length === 0) {
    return <NotificationEmptyState />;
  }

  return (
    <div className="flex flex-col gap-2">
      <span>
        <h2 className="mb-4 text-xl font-medium text-primary">{t`Notifications`}</h2>
        <hr />
      </span>
      <ul ref={parentRef}>
        {slicedNotifications.map((notification, index) => (
          <li key={notification.id}>
            <Notification notification={notification} />
            {index !== ITEMS_PER_PAGE - 1 && notification.id !== notifications[notifications.length - 1].id && <hr />}
          </li>
        ))}
      </ul>
      {notifications.length > ITEMS_PER_PAGE && (
        <Pagination page={page} count={Math.ceil(notifications.length / ITEMS_PER_PAGE)} onChange={setPage} />
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/documents/notification")({
  component: NotificationListView,
  context: () => ({ permissions: "document.notification" }),
});
